import { useCallback, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNewBlockAddedHandler } from './useNewBlockAddedHandler';
import { useCheckDocumentLockStatus } from './useCheckDocumentLockStatus';
import { SelectionContext } from '../GridDndEditor/SelectedBlockInfoProvider';
import type { GridBlockType } from 'services/entities/BlockTypes';
import { setActiveSidePanel, setIsAdvancedSpacingModelOpen } from '../grid/reduxStore/blockStyleSettingsSlice';
import { getCanvasWidth, gridDefaultDimensionsOnDrop } from '../shared/gridConfig';
import useActiveSection from '../SectionsDrawer/useActiveSection';
import { selectSections } from '../grid/reduxStore/editorSlice';
import type { Block } from '../GridDndEditor/models/Block.model';
import { getCenteredY, getGreaterZIndexAvailable, resolveTargetSectionId } from './sectionViewportUtils';
import { useEffectiveOrientation } from 'hooks/useEffectiveOrientation';
import { DocumentSettingsContext } from '../SidePanel/DocumentSettings/DocumentDesignSettings/DocumentSettingsContext';
import useDeferredDispatch from './useDeferredDispatch';
import type { CreateBlockData } from '../UndoRedo/CreateBlockCommand';

export type ClickToAddSimpleBlockConfig = {
  type: GridBlockType;
  content: CreateBlockData;
  // Whether the block's default width gets clamped to the available content-area width.
  // Embed's/Video's does; Shape's currently doesn't — an existing inconsistency this hook
  // preserves as-is rather than resolving. Pass the config that matches each type's real,
  // shipped behavior, not what "should" be consistent.
  clampWidth: boolean;
};

// Shared body for the click-to-add hooks whose block types only differ by data, not control
// flow (currently Embed, Video, and Shape). Table and Image are NOT candidates for this: Table has a
// different argument shape and a different side-panel dispatch enum (TableSettingsTypes, not
// GridBlockType), and Image does async pre-work (upload + dimension probe) before section
// resolution even starts — folding either in would add branching here, not remove it.
export default function useClickToAddSimpleBlock(config: ClickToAddSimpleBlockConfig) {
  const { selectedSectionId, setSelectedBlockInfoByWrapper } = useContext(SelectionContext);
  const { activeSection } = useActiveSection();
  const sections = useSelector(selectSections);
  const blockAddedHandler = useNewBlockAddedHandler();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();
  const deferredDispatch = useDeferredDispatch();
  const { documentSettings } = useContext(DocumentSettingsContext);
  const isAddingRef = useRef(false);
  const canvasWidth = getCanvasWidth(useEffectiveOrientation());
  const canvasWidthRef = useRef(canvasWidth);
  canvasWidthRef.current = canvasWidth;

  return useCallback(async () => {
    if (isAddingRef.current) return null;
    isAddingRef.current = true;

    try {
      const targetSectionId = resolveTargetSectionId(selectedSectionId, activeSection, sections);

      if (!targetSectionId) return null;

      const { margin } = documentSettings;
      const availableWidth = canvasWidthRef.current - margin.left - margin.right;
      const { width: defaultWidth, height } = gridDefaultDimensionsOnDrop[config.type];

      const width = config.clampWidth ? Math.min(defaultWidth, availableWidth) : defaultWidth;
      const x = margin.left + (availableWidth - width) / 2;
      const blockConfig: Block = {
        x,
        y: getCenteredY(targetSectionId, height),
        z: getGreaterZIndexAvailable(targetSectionId),
        width,
        height,
      };

      const response = await blockAddedHandler(targetSectionId, config.content, blockConfig, config.type);

      if (checkDocumentLockStatus(response.errorCode)) return null;

      const blockId = response.content.blockId;
      if (!blockId) return null;

      setSelectedBlockInfoByWrapper({ blockId, sectionId: targetSectionId });
      deferredDispatch(setActiveSidePanel({ type: config.type }), setIsAdvancedSpacingModelOpen(false));

      return blockId;
    } finally {
      isAddingRef.current = false;
    }
  }, [
    activeSection,
    blockAddedHandler,
    checkDocumentLockStatus,
    deferredDispatch,
    sections,
    selectedSectionId,
    setSelectedBlockInfoByWrapper,
    documentSettings,
    config,
  ]);
}
